import { FC } from 'react';
import { match } from 'ts-pattern';
import { colors } from 'ui/theme';
import {
  FrequencyManagementMessageType,
  FrequencyMode,
  SupportedMediums,
} from 'modules/frequencyManagement/lib/types';
import { lowerCaseMessageMedium } from 'modules/frequencyManagement/lib/utils/mediumDomainUtils';
import { sortMessageTypes } from 'modules/frequencyManagement/lib/utils/mediumDomainUtils';
import { ConfigByMedium } from 'modules/frequencyManagement/lib/types';
import {
  Wrapper,
  Binoculars24Margin,
  FlexHeading,
} from 'modules/frequencyManagement/lib/components/summaryBanner/SummaryBanner';
import { MessageTypesBreakdown } from 'modules/frequencyManagement/lib/components/summaryBanner/MessageTypesBreakdown';
import { ManageSettingsLink } from 'modules/frequencyManagement/lib/components/summaryBanner/ManageSettingsLink';

export const getOptimizedMessageTypesSummary = (
  config: ConfigByMedium,
  messageMedium: SupportedMediums,
  messageTypesForMedium: Array<FrequencyManagementMessageType>,
) => {
  const mediumIsOptimize = config[messageMedium].settings?.mode === FrequencyMode.Optimize;
  /** Ensure message types names are sorted in list */
  const messageTypes = sortMessageTypes(messageTypesForMedium);
  const summary = {
    offNames: [] as Array<string>,
    onNames: [] as Array<string>,
  };
  return match({ mediumIsOptimize })
    .with({ mediumIsOptimize: true }, () => {
      return messageTypes.reduce((acc, messageType) => {
        if (messageType.frequencyManagement.settings?.mode === FrequencyMode.Optimize) {
          return {
            ...acc,
            onNames: acc.onNames.concat(messageType.name),
          };
        } else if (messageType.frequencyManagement.settings?.mode === FrequencyMode.Off) {
          return {
            ...acc,
            offNames: acc.offNames.concat(messageType.name),
          };
        }
        return acc;
      }, summary);
    })
    .with({ mediumIsOptimize: false }, () => {
      return messageTypes.reduce((acc, messageType) => {
        if (messageType.frequencyManagement.settings?.mode === FrequencyMode.Optimize) {
          return {
            ...acc,
            onNames: acc.onNames.concat(messageType.name),
          };
        }
        return acc;
      }, summary);
    })
    .exhaustive();
};

export const formatOptimizedMessageTypeOwnRulesText = (messageTypeCount: number) => {
  if (messageTypeCount === 0) {
    return 'currently have their own rules';
  }

  return messageTypeCount !== 1 ? 'currently have their own rules' : 'currently has its own rules';
};

export const formatOptimizedMessageTypeFollowRulesText = (
  messageMedium: SupportedMediums,
  messageTypeCount: number,
) => {
  const s = messageTypeCount === 1 ? 's' : '';
  return `currently follow${s} the ${lowerCaseMessageMedium(messageMedium)} frequency optimization rule`;
};

export interface BreakdownSummaryBanner {
  messageMedium: SupportedMediums;
  config: ConfigByMedium;
  asExternalContent?: boolean;
}
export const BreakdownSummaryBanner: FC<BreakdownSummaryBanner> = ({ config, messageMedium }) => {
  const messageTypes = config[messageMedium].messageTypes || [];
  const { onNames, offNames } = getOptimizedMessageTypesSummary(
    config,
    messageMedium,
    messageTypes,
  );
  return (
    <Wrapper data-external={true}>
      <FlexHeading>
        <Binoculars24Margin color={colors.iconPrimary} />
        <MessageTypesBreakdown
          asExternalContent={true}
          followMediumRuleTextFormatter={formatOptimizedMessageTypeFollowRulesText}
          isForOnType={false}
          messageMedium={messageMedium}
          names={offNames}
        />
        <MessageTypesBreakdown
          asExternalContent={true}
          ownRulesTextFormatter={formatOptimizedMessageTypeOwnRulesText}
          isForOnType={true}
          messageMedium={messageMedium}
          names={onNames}
        />
      </FlexHeading>
      <ManageSettingsLink messageMedium={messageMedium} />
    </Wrapper>
  );
};
